import store from 'store';
import { SERVER_ERROR } from '../constants/messages';
import { register } from '../dispatchers/dispatcher';
import BaseStore from './base-store';
import PostHogService from '../services/posthog';
import PostHogEvents from '../constants/posthog-events';
import AppAuthConstants from '../constants/auth-constants'
import authAPI from '../api/authApi';
import Analytics from '../services/analytics';

// how stale the saved user can get before returning to the tab fetches it again
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
// the parts of the saved user the API owns; a refresh only announces a change when one differs
const REFRESHED_FIELDS = ['account_type', 'free_trial_expired', 'free_trial_expires_at', 'name', 'email', 'receive_messages', 'is_academic'];

const withoutAdmin = ({ admin, ...user }) => user;

class LoginStore extends BaseStore {
  constructor() {
    super();
    this.subscribe(this.dispatcherIndex.bind(this));
    this.errors = null;
    this.key = "3D9C1EDB38DABE27";
  }

  dispatcherIndex() {
    this.errors = null;

    register((action) => {
      this.errors = null;

      switch (action.actionType) {

        case AppAuthConstants.REQUEST_LOGIN_USER:
          authAPI.attemptLogin(action.email, action.password);
          this.emitChange();
          break;

        case AppAuthConstants.REQUEST_LOGIN_USER_SUCCESS:
          // the login event itself is sent by MarketingLogin, which reloads the page
          Analytics.identifyUser(action.data);

          PostHogService.identifyUser(action.data);
          PostHogService.capture(PostHogEvents.USER_LOGGED_IN);

          // sign-in also returns admin status for the backoffice; the app has no use for it
          authAPI.storeSessionUser(this.key, withoutAdmin(action.data));
          this.emitChange();
          break;

        case AppAuthConstants.REQUEST_LOGIN_USER_ERROR:
          this.errors = action.error || SERVER_ERROR;
          this.emitChange();
          break;

        case AppAuthConstants.REQUEST_LOGOUT_USER:
          authAPI.logout(action.headers, action.redirectPath);
          PostHogService.reset();
          Analytics.reset();
          store.clearAll();
          break;

        case AppAuthConstants.REQUEST_LOGOUT_USER_SUCCESS:
          store.clearAll();
          authAPI.changeLocation(action.redirectPath || '/');
          break;

        case AppAuthConstants.REQUEST_SIGNUP_USER:
          authAPI.attemptRegister(action.data);
          this.emitChange();
          break;

        case AppAuthConstants.REQUEST_SIGNUP_USER_SUCCESS:
          // signing up starts the 7-day free trial
          Analytics.identifyUser(action.data);
          Analytics.event('sign_up', { method: 'email' });

          PostHogService.identifyUser(action.data);
          PostHogService.capture(PostHogEvents.USER_SIGNED_UP);

          authAPI.storeSessionUser(this.key, action.data);
          this.emitChange({ modal: 'thank-you' });
          break;

        case AppAuthConstants.REQUEST_SIGNUP_USER_ERROR:
          this.errors = action.error || SERVER_ERROR;
          this.emitChange();
          break;

        case AppAuthConstants.USER_DETAILS_UPDATED:
          // Re-identify to update properties
          PostHogService.identifyUser(action.data);
          PostHogService.capture(PostHogEvents.USER_PROFILE_UPDATED);

          authAPI.updateSessionUserData(this.key, action.data);
          // the profile response may be partial, so use the merged session data
          Analytics.identifyUser(this.getSessionUserData());
          this.emitChange({ success: true });
          break;

        case AppAuthConstants.USER_DETAILS_UPDATE_ERROR:
          this.emitChange({ success: false });
          break;

        case AppAuthConstants.REQUEST_FORGOT_PASSWORD_VIEW:
          this.emitChange({ modal: 'forgot-password' });
          break;

        case AppAuthConstants.CHECKLISTVIEW:
          authAPI.updateSessionUserData(this.key, action.data);
          this.emitChange();
          break;
      }
    });

  }

  // The session keeps the copy of the user from sign-in, so an upgrade on another device or
  // an admin grant would otherwise only show after signing in again. Refresh it on load and
  // when the visitor comes back to the tab.
  watchForUserChanges() {
    if (this.isLoggedIn()) {
      // identify straight away from the saved copy; the refresh re-identifies if it changed
      const user = this.getSessionUserData();
      if (user.id) {
        PostHogService.identifyUser(user);
        Analytics.identifyUser(user);
      }
    }

    this.refreshCurrentUser();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && Date.now() - (this.lastRefreshAt || 0) > REFRESH_INTERVAL_MS) {
        this.refreshCurrentUser();
      }
    });
  }

  refreshCurrentUser() {
    if (!this.isLoggedIn() || this.refreshing) return;

    this.refreshing = true;
    this.lastRefreshAt = Date.now();

    const before = this.getSessionUserData();
    const headers = { 'X-USER-EMAIL': before.email, 'X-USER-TOKEN': before.authentication_token };
    authAPI.getProfile(headers, {
      onSuccess: ({ data }) => {
        this.refreshing = false;
        if (!data || !data.id) return;

        // also drops admin from sessions saved before the app stopped keeping it
        authAPI.storeSessionUser(this.key, withoutAdmin({ ...before, ...data }));
        const after = this.getSessionUserData();
        if (before.id && REFRESHED_FIELDS.every(field => before[field] === after[field])) return;

        PostHogService.identifyUser(after);
        Analytics.identifyUser(after);
        this.emitChange({ refreshed: true });
      },
      onError: ({ response }) => {
        this.refreshing = false;
        // the token no longer works (expired, or revoked by a password change elsewhere), so
        // sign out here too; reloading shows the page signed out, or redirects to sign in
        if (response && response.statusCode === 401) {
          store.clearAll();
          window.location.reload();
        }
      },
    });
  }

  getSessionToken() {
    return authAPI.getSessionToken(this.key)
  }
  getSessionUser() {
    return authAPI.getSessionUser(this.key)
  }

  getSessionUserData() {
    return authAPI.getSessionUserData(this.key)
  }

  getLoginErrors() {
    return this._error;
  }

  isLoggedIn() {
    return authAPI.isLoggedIn(this.key);
  }

  updateSessionUserData(data, callbacks) {
    return authAPI.updateSessionUserData(this.key, data, callbacks);
  }

  updateUser(headers, data, callbacks) {
    return authAPI.updateUser(headers, data, callbacks);
  }

  updateUserPassword(headers, data, callbacks) {
    return authAPI.updateUserPassword(headers, data, callbacks);
  }

  verifyToken(token, callbacks) {
    return authAPI.verifyToken(token, callbacks);
  }

}

export default new LoginStore();
